import React from 'react';
import { Target } from 'lucide-react';
import HelpTip from '../../../components/HelpTip';

const LINE_META = {
  placement: { label: 'Referral & Placement', help: 'placement', marquee: true },
  programs: { label: 'DOLE Programs', help: 'programs' },
  lmi: { label: 'LMI Reports (on time)', help: 'lmi' },
  career: { label: 'Career Guidance', help: 'career' },
  awards: { label: 'Awards', help: 'awards' },
  leadership: { label: 'Leadership', help: 'leadership' },
};

function tier(points, max) {
  const r = max ? points / max : 0;
  if (r >= 0.9) return { ring: '#059669', bg: 'bg-emerald-50', bd: 'border-emerald-200', tx: 'text-emerald-700' };
  if (r >= 0.6) return { ring: '#0284c7', bg: 'bg-sky-50', bd: 'border-sky-200', tx: 'text-sky-700' };
  if (r >= 0.33) return { ring: '#d97706', bg: 'bg-amber-50', bd: 'border-amber-200', tx: 'text-amber-700' };
  return { ring: '#e11d48', bg: 'bg-rose-50', bd: 'border-rose-200', tx: 'text-rose-700' };
}

function Ring({ pct, color, label, sub }) {
  const R = 26;
  const C = 2 * Math.PI * R;
  const off = C * (1 - pct / 100);
  return (
    <svg width="70" height="70" viewBox="0 0 72 72" className="shrink-0">
      <circle cx="36" cy="36" r={R} fill="none" strokeWidth="7" stroke="#e2e8f0" />
      <circle cx="36" cy="36" r={R} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={C} strokeDashoffset={off} transform="rotate(-90 36 36)" />
      <text x="36" y="34" textAnchor="middle" fontSize="16" fontWeight="800" fill="#0f172a" className="tabular-nums">{label}</text>
      <text x="36" y="48" textAnchor="middle" fontSize="9" fill="#64748b">{sub}</text>
    </svg>
  );
}

export default function SeasonScorecard({ season }) {
  if (!season) return null;
  return (
    <div>
      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-1">
        Season scorecard — points toward the award
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        Each line shows your points, the DOLE benchmark for full marks, and the next milestone.
        Verified against the DOLE-9 “Points System Reference”.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {season.lines.map((l) => {
          const meta = LINE_META[l.key] || { label: l.key };
          const tc = tier(l.points, l.max);
          const pct = l.max ? (l.points / l.max) * 100 : 0;
          const paceTag = l.onPacePoints != null && l.onPacePoints !== l.points
            ? <span className="text-[11px] text-blue-700 font-semibold">↑ {l.onPacePoints} on pace</span> : null;
          return (
            <div key={l.key} className={`rounded-xl border border-slate-200 bg-white p-4 ${meta.marquee ? 'ring-1 ring-blue-200' : ''}`}>
              <div className="flex items-center gap-4">
                <Ring pct={pct} color={tc.ring} label={`${l.points}`} sub={`of ${l.max}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h4 className="font-semibold text-sm text-slate-900">{meta.label}</h4>
                    <HelpTip id={meta.help} />
                    {meta.marquee && <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">MARQUEE</span>}
                  </div>
                  <div className="text-lg font-extrabold tabular-nums text-slate-900 mt-0.5">{l.value}</div>
                  <div className="text-[11px] text-slate-500">{l.valueSub}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Benchmark: {l.benchmark} → {l.max} pts</div>
                  {l.next && (
                    <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md border ${tc.bg} ${tc.bd} ${tc.tx}`}>
                      <Target className="h-3 w-3" /> {l.next}
                    </div>
                  )}
                  {paceTag && <div className="mt-1">{paceTag}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
